import { PlusIcon } from "@phosphor-icons/react/dist/csr/Plus";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@volca/ui/components/ui/input-group";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import { AP_PALETTE } from "@/core/colors";

const Chip = ({ value, onRemove }: { value: string; onRemove: () => void }) => (
  <Badge
    variant="secondary"
    color={AP_PALETTE.neutral.tone}
    size="lg"
    className="gap-1 pr-0.5 font-normal"
  >
    <span>{value}</span>
    {/* 24px target (WCAG 2.5.8) with a name, not an anonymous ✕. */}
    <Button
      variant="ghost"
      size="icon-xs"
      aria-label={`Remove ${value}`}
      onClick={onRemove}
      className="rounded-full text-subtle"
    >
      <XIcon className="h-3.5 w-3.5" aria-hidden />
    </Button>
  </Badge>
);

/**
 * Removable chips with a quiet "+ Add …" under them, where the new chip will
 * appear. Enter adds; Escape (or clicking away while empty) closes the input.
 * Removing saves at once, so it offers Undo in a toast.
 */
export const EditableChips = ({
  values,
  onAdd,
  onRemove,
  noun,
  placeholder,
}: {
  values: string[];
  onAdd: (value: string) => void;
  onRemove: (value: string) => void;
  /** Singular, lower case: "address", "domain". */
  noun: string;
  placeholder: string;
}) => {
  const [draft, setDraft] = useState("");
  const [adding, setAdding] = useState(false);

  const add = () => {
    const value = draft.trim();
    if (value) onAdd(value);
    setDraft("");
  };

  const remove = (value: string) => {
    onRemove(value);
    toast(`Removed ${value}`, {
      action: {
        label: "Undo",
        onClick: () => onAdd(value),
      },
    });
  };

  return (
    <Stack className="w-full" align="start" gap={2}>
      {values.length > 0 ? (
        <Stack direction="row" gap={2} align="center" className="flex-wrap">
          {values.map((v) => (
            <Chip key={v} value={v} onRemove={() => remove(v)} />
          ))}
        </Stack>
      ) : (
        !adding && <Text tone="muted">None</Text>
      )}

      {adding ? (
        <InputGroup>
          <InputGroupInput
            autoFocus
            aria-label={`New ${noun}`}
            value={draft}
            placeholder={placeholder}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => !draft.trim() && setAdding(false)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                add();
              }
              if (e.key === "Escape") {
                setDraft("");
                setAdding(false);
              }
            }}
          />
          <InputGroupAddon align="inline-end">
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Add ${draft.trim() || noun}`}
              onClick={add}
              disabled={!draft.trim()}
              className="text-subtle"
            >
              <PlusIcon className="h-5 w-5" aria-hidden />
            </Button>
          </InputGroupAddon>
        </InputGroup>
      ) : (
        <Button
          variant="link"
          size="sm"
          className="self-start px-0 has-data-[icon=inline-start]:pl-0"
          onClick={() => setAdding(true)}
        >
          <PlusIcon data-icon="inline-start" aria-hidden />
          Add {noun}
        </Button>
      )}
    </Stack>
  );
};
